/**
 * Canonical state hashing.
 *
 * The point of a state hash is to turn "did these two runs agree?" from a
 * judgement call into a string comparison. That only works if the hash is a
 * function of the *simulation's decisions* and of nothing else — not of key
 * insertion order, not of floating-point noise below the resolution anyone
 * cares about, not of whether a value happens to be `undefined` or absent.
 */

/** Values that may appear in a canonical state tree. */
export type Canonical =
  | null
  | boolean
  | number
  | string
  | Canonical[]
  | { [key: string]: Canonical };

/**
 * Decimal places retained when hashing a float.
 *
 * Positions and hunger levels are continuous quantities that accumulate tiny
 * differences from operation *ordering* even when every input is identical.
 * Six places is far finer than any decision the simulation makes (thresholds
 * compare at 0.01 scale) and coarse enough to be robust.
 */
const FLOAT_PRECISION = 6;

/**
 * Serialise a value to a canonical string.
 *
 * Object keys are sorted, so two states that differ only in property insertion
 * order hash identically. Arrays keep their order, because array order in this
 * codebase is meaningful.
 */
export function canonicalize(value: Canonical): string {
  if (value === null) return 'null';

  switch (typeof value) {
    case 'boolean':
      return value ? 'true' : 'false';

    case 'number': {
      if (Number.isNaN(value)) return 'NaN';
      if (!Number.isFinite(value)) return value > 0 ? 'Inf' : '-Inf';
      if (Number.isInteger(value)) return String(value);
      // `toFixed` then strip trailing zeros, so 1.5 and 1.500000 agree, and
      // -0 does not sneak in as a distinct value from 0.
      const fixed = value.toFixed(FLOAT_PRECISION).replace(/\.?0+$/, '');
      return fixed === '-0' ? '0' : fixed;
    }

    case 'string':
      return JSON.stringify(value);

    default:
      break;
  }

  if (Array.isArray(value)) {
    return `[${value.map(canonicalize).join(',')}]`;
  }

  const keys = Object.keys(value).sort();
  const parts: string[] = [];
  for (const key of keys) {
    const v = value[key];
    // Absent and explicitly-undefined must not be distinguishable.
    if (v === undefined) continue;
    parts.push(`${JSON.stringify(key)}:${canonicalize(v)}`);
  }
  return `{${parts.join(',')}}`;
}

/**
 * FNV-1a, 64-bit, computed in two 32-bit halves.
 *
 * Not cryptographic — nothing here is defending against an adversary. It needs
 * to be fast, stable across engines, and wide enough that an accidental
 * collision between two genuinely different colony states is not a thing that
 * happens. 64 bits is comfortably enough for that.
 */
export function hashString(input: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;

  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193);
    h2 ^= c + i;
    h2 = Math.imul(h2, 0x85ebca6b);
  }

  // Final avalanche, so a one-character change moves most output bits.
  h1 ^= h1 >>> 15;
  h1 = Math.imul(h1, 0x2545f491);
  h1 ^= h1 >>> 13;
  h2 ^= h2 >>> 16;
  h2 = Math.imul(h2, 0xc2b2ae35);
  h2 ^= h2 >>> 16;

  const hex = (n: number) => (n >>> 0).toString(16).padStart(8, '0');
  return hex(h1) + hex(h2);
}

/** Hash a canonical state tree. Stable across runs, processes and runtimes. */
export function hashState(state: Canonical): string {
  return hashString(canonicalize(state));
}
